import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listClients } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useAuth } from '../lib/auth';
import { useIsMobile } from '../lib/useIsMobile';
import { GBIBadge, GBIBtn, GBICard, GBILabel, T, sharedStyles } from '../components/Shared';
import { NewClientModal } from '../components/NewClientModal';
import { MaintenancePanel } from '../components/MaintenancePanel';
import {
  buildBusinessSummaries, DEFAULT_CLIENT_SORT, filterBusinesses, getClientFilterOptions,
  isClientSortKey, sortBusinesses, type BusinessSummary, type BusinessTypeFilter, type ClientSortKey,
} from '../lib/client-list';

type ViewMode = 'card' | 'list';

export function AdminClientsList() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showNewClient, setShowNewClient] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [view, setView] = useState<ViewMode>(
    () => {
      const saved = localStorage.getItem('gbi.clientsView');
      return saved === 'list' || saved === 'card' ? saved : 'card';
    },
  );
  const [sort, setSort] = useState<ClientSortKey>(() => {
    const saved = localStorage.getItem('gbi.clientsSort');
    return isClientSortKey(saved) ? saved : DEFAULT_CLIENT_SORT;
  });
  const [query, setQuery] = useState('');
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [businessType, setBusinessType] = useState<BusinessTypeFilter>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const state = useAsync(listClients, [reloadKey]);
  const isMobile = useIsMobile();

  const businesses = useMemo(
    () => state.status === 'success' ? buildBusinessSummaries(state.data) : [],
    [state],
  );
  const filterOptions = useMemo(() => getClientFilterOptions(businesses), [businesses]);
  const visibleBusinesses = useMemo(() => sortBusinesses(filterBusinesses(businesses, {
    query, locations: selectedLocations, categories: selectedCategories, businessType,
  }), sort), [businesses, query, selectedLocations, selectedCategories, businessType, sort]);
  const locationCount = state.status === 'success' ? state.data.length : 0;
  const visibleLocationCount = visibleBusinesses.reduce((total, business) => total + business.locations.length, 0);
  const hasFilters = selectedLocations.length > 0 || selectedCategories.length > 0 || businessType !== 'all';

  const setViewPersisted = (v: ViewMode) => {
    setView(v);
    localStorage.setItem('gbi.clientsView', v);
  };

  const setSortPersisted = (next: ClientSortKey) => {
    setSort(next);
    localStorage.setItem('gbi.clientsSort', next);
  };

  const clearFilters = () => {
    setSelectedLocations([]);
    setSelectedCategories([]);
    setBusinessType('all');
  };

  return (
    // Own scroll container: global CSS locks body{overflow:hidden} on desktop,
    // so the page must scroll itself or long client lists get clipped.
    <div style={{ height: '100vh', overflowY: 'auto', background: '#f8f9fa', padding: isMobile ? '20px 14px' : '40px 32px' }}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{
              fontFamily: sharedStyles.fontDisplay, fontWeight: 700, fontSize: 11,
              background: T.brandGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6,
            }}>AI Intelligence — Internal Console</div>
            <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 28, fontWeight: 700, color: T.fgPrimary }}>
              Clients
            </div>
            <div style={{ fontSize: 13, color: T.fgSecondary, marginTop: 4 }}>
              {state.status === 'success'
                ? `${businesses.length} businesses · ${locationCount} locations · Click a business to view its dashboard`
                : 'Loading…'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <button onClick={() => setShowMaintenance(true)} style={{
                background: T.cyan, border: `1px solid ${T.cyan}`, color: '#fff',
                borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                fontSize: 13, fontWeight: 600,
              }}>Check &amp; Update</button>
            )}
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <Link to="/admin/status" style={{ textDecoration: 'none' }}>
                <button style={{
                  background: 'transparent', border: `1px solid ${T.border}`, color: T.fgSecondary,
                  borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                  fontSize: 13,
                }}>Client Status</button>
              </Link>
            )}
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <Link to="/quick-audit" style={{ textDecoration: 'none' }}>
                <button style={{
                  background: 'transparent', border: `1px solid ${T.border}`, color: T.fgSecondary,
                  borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                  fontSize: 13,
                }}>Quick Audit</button>
              </Link>
            )}
            {user && user.role !== 'client' && (
              <Link to="/admin/gbp" style={{ textDecoration: 'none' }}>
                <button style={{
                  background: 'transparent', border: `1px solid ${T.border}`,
                  borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                  fontSize: 13, color: T.fgSecondary,
                }}>GBP Management</button>
              </Link>
            )}
            {user && user.role !== 'client' && user.allowedPages.includes('intake') && (
              <Link to="/admin/intake" style={{ textDecoration: 'none' }}>
                <button style={{
                  background: 'transparent', border: `1px solid ${T.border}`,
                  borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                  fontSize: 13, color: T.fgSecondary,
                }}>Client Intake</button>
              </Link>
            )}
            {user?.role === 'admin' && (
              <Link to="/admin/users" style={{ textDecoration: 'none' }}>
                <button style={{
                  background: 'transparent', border: `1px solid ${T.border}`,
                  borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                  fontSize: 13, color: T.fgSecondary,
                }}>Users</button>
              </Link>
            )}
            {user?.role === 'admin' && (
              <GBIBtn variant="primary" onClick={() => setShowNewClient(true)}>+ New Client</GBIBtn>
            )}
          </div>
        </div>

        {/* Tiny logged-in indicator + sign out */}
        {user && (
          <div style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
            gap: 12, marginBottom: 16, fontSize: 12, color: T.fgMuted,
          }}>
            <span>Signed in as <strong style={{ color: T.fgPrimary }}>{user.email}</strong> ({user.role})</span>
            <button onClick={logout} style={{
              background: 'transparent', border: `1px solid ${T.border}`,
              borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
              fontSize: 11, color: T.fgSecondary,
            }}>Sign out</button>
          </div>
        )}

        {/* Toolbar: search, sort, filter + view toggle */}
        {state.status === 'success' && state.data.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            marginBottom: 14, flexWrap: 'wrap',
          }}>
            <div style={{ position: 'relative', flex: '0 1 340px', minWidth: 220 }}>
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search clients by name, location, or group…"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '8px 10px', fontSize: 13,
                  border: `1px solid ${T.border}`, borderRadius: 8,
                  outline: 'none', background: '#fff', color: T.fgPrimary,
                }}
              />
            </div>
            <select value={sort} onChange={e => setSortPersisted(e.target.value as ClientSortKey)} aria-label="Sort businesses" style={{
              minWidth: 178, padding: '8px 10px', fontSize: 12, background: '#fff', color: T.fgPrimary,
              border: `1px solid ${T.border}`, borderRadius: 8, cursor: 'pointer',
            }}>
              <option value="name_asc">Name · A–Z</option>
              <option value="name_desc">Name · Z–A</option>
              <option value="locations_desc">Locations · most first</option>
              <option value="locations_asc">Locations · fewest first</option>
              <option value="location_asc">Primary location · A–Z</option>
              <option value="location_desc">Primary location · Z–A</option>
              <option value="updated_desc">Recently updated</option>
            </select>
            <div style={{ position: 'relative' }}>
              <button onClick={() => setFiltersOpen(open => !open)} style={{
                background: filtersOpen || hasFilters ? 'rgba(18,181,196,0.10)' : '#fff', color: hasFilters ? T.cyanDim : T.fgSecondary,
                border: `1px solid ${hasFilters ? T.cyan : T.border}`, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              }}>Filter{hasFilters ? ` (${selectedLocations.length + selectedCategories.length + (businessType === 'all' ? 0 : 1)})` : ''}</button>
              {filtersOpen && <FilterMenu
                locations={filterOptions.locations}
                categories={filterOptions.categories}
                selectedLocations={selectedLocations}
                selectedCategories={selectedCategories}
                businessType={businessType}
                onToggleLocation={value => setSelectedLocations(values => toggleValue(values, value))}
                onToggleCategory={value => setSelectedCategories(values => toggleValue(values, value))}
                onBusinessType={setBusinessType}
                onClear={clearFilters}
                onClose={() => setFiltersOpen(false)}
              />}
            </div>
            {/* Segmented view switcher */}
            <div style={{
              display: 'flex', border: `1px solid ${T.border}`, borderRadius: 8,
              overflow: 'hidden',
            }}>
              {([
                { id: 'card' as ViewMode, label: 'Card', },
                { id: 'list' as ViewMode, label: 'List' },
              ]).map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setViewPersisted(opt.id)}
                  style={{
                    padding: '8px 16px', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', border: 'none',
                    background: view === opt.id ? T.cyan : '#fff',
                    color: view === opt.id ? '#fff' : T.fgSecondary,
                  }}
                >{opt.label}</button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <GBILabel style={{ marginBottom: 0 }}>All Clients</GBILabel>
          {state.status === 'success' && <span style={{ fontSize: 12, color: T.fgMuted }}>
            {visibleBusinesses.length} of {businesses.length} businesses · {visibleLocationCount} locations
          </span>}
        </div>
        {hasFilters && (
          <ActiveFilters locations={selectedLocations} categories={selectedCategories} businessType={businessType}
            onRemoveLocation={value => setSelectedLocations(values => values.filter(item => item !== value))}
            onRemoveCategory={value => setSelectedCategories(values => values.filter(item => item !== value))}
            onClear={clearFilters} />
        )}

        {state.status === 'loading' && (
          <div style={{ padding: 40, textAlign: 'center', color: T.fgMuted, fontSize: 13 }}>Loading clients…</div>
        )}

        {state.status === 'error' && (
          <GBICard style={{ borderColor: T.magenta, color: T.magenta }}>
            <strong>Failed to load clients.</strong>
            <div style={{ fontSize: 12, marginTop: 6, fontFamily: sharedStyles.fontMono }}>{state.error.message}</div>
            <div style={{ fontSize: 12, marginTop: 8, color: T.fgSecondary }}>
              Tip: this needs Pages Functions running. Use <code>npx wrangler pages dev</code> locally,
              or it works automatically on the deployed site.
            </div>
          </GBICard>
        )}

        {showNewClient && (
          <NewClientModal
            onClose={() => setShowNewClient(false)}
            onCreated={c => {
              setShowNewClient(false);
              setReloadKey(k => k + 1);
              // Jump straight to the new client so the user can immediately
              // click Run All to populate Places / Maps / AI data.
              navigate(`/admin/clients/${c.id}`);
            }}
          />
        )}

        {showMaintenance && <MaintenancePanel clients={state.status === 'success' ? state.data : []} onReload={() => setReloadKey(key => key + 1)} onClose={() => setShowMaintenance(false)} />}

        {state.status === 'success' && (
          state.data.length === 0 ? (
            <GBICard>
              <div style={{ color: T.fgMuted, fontSize: 13 }}>No clients yet. Click "+ New Client" to add one.</div>
            </GBICard>
          ) : (
            <GroupedClients businesses={visibleBusinesses} view={view} query={query} hasActiveCriteria={hasFilters || Boolean(query.trim())} />
          )
        )}
      </div>
    </div>
  );
}

// Renders one business card per head client. Additional client rows remain
// selectable locations inside that business dashboard.
function GroupedClients({ businesses, view, query, hasActiveCriteria }: {
  businesses: BusinessSummary[];
  view: ViewMode;
  query: string;
  hasActiveCriteria: boolean;
}) {
  if (businesses.length === 0) {
    return (
      <GBICard>
        <div style={{ color: T.fgMuted, fontSize: 13, fontStyle: 'italic' }}>
          {hasActiveCriteria ? `No businesses match${query.trim() ? ` "${query}"` : ' these filters'}.` : 'No businesses yet.'}
        </div>
      </GBICard>
    );
  }

  if (view === 'list') {
    return <BusinessListTable businesses={businesses} />;
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, alignItems: 'stretch' }}>
      {businesses.map(b => <BusinessCard key={b.head.id} biz={b} />)}
    </div>
  );
}

function FilterMenu({
  locations, categories, selectedLocations, selectedCategories, businessType,
  onToggleLocation, onToggleCategory, onBusinessType, onClear, onClose,
}: {
  locations: string[];
  categories: string[];
  selectedLocations: string[];
  selectedCategories: string[];
  businessType: BusinessTypeFilter;
  onToggleLocation: (value: string) => void;
  onToggleCategory: (value: string) => void;
  onBusinessType: (value: BusinessTypeFilter) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  return (
    <div style={{
      position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 20, width: 320,
      maxWidth: 'calc(100vw - 28px)', background: '#fff', border: `1px solid ${T.border}`,
      borderRadius: 10, boxShadow: '0 10px 28px rgba(0,0,0,0.14)', padding: 14,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <strong style={{ fontSize: 13, color: T.fgPrimary }}>Filter businesses</strong>
        <button onClick={onClose} aria-label="Close filters" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: T.fgMuted, fontSize: 16 }}>×</button>
      </div>
      <FilterSection label="Business type">
        <select value={businessType} onChange={event => onBusinessType(event.target.value as BusinessTypeFilter)} style={filterSelectStyle}>
          <option value="all">All businesses</option>
          <option value="single">Single-location</option>
          <option value="multi">Multi-location</option>
        </select>
      </FilterSection>
      <FilterSection label="Locations">
        <OptionChecklist options={locations} selected={selectedLocations} onToggle={onToggleLocation} emptyLabel="No locations available" />
      </FilterSection>
      <FilterSection label="Categories">
        <OptionChecklist options={categories} selected={selectedCategories} onToggle={onToggleCategory} emptyLabel="No categories available" />
      </FilterSection>
      <button onClick={onClear} style={{ background: 'transparent', border: 'none', color: T.cyanDim, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>Clear all filters</button>
    </div>
  );
}

const filterSelectStyle: React.CSSProperties = {
  width: '100%', padding: '7px 8px', border: `1px solid ${T.border}`, borderRadius: 6,
  background: '#fff', color: T.fgPrimary, fontSize: 12,
};

function FilterSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 13 }}>
      <div style={{ color: T.fgMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

function OptionChecklist({ options, selected, onToggle, emptyLabel }: {
  options: string[]; selected: string[]; onToggle: (value: string) => void; emptyLabel: string;
}) {
  if (options.length === 0) return <div style={{ fontSize: 12, color: T.fgMuted, fontStyle: 'italic' }}>{emptyLabel}</div>;
  return (
    <div style={{ maxHeight: 116, overflowY: 'auto', border: `1px solid ${T.border}`, borderRadius: 6, padding: '3px 6px' }}>
      {options.map(option => (
        <label key={option} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 0', fontSize: 12, color: T.fgPrimary, cursor: 'pointer' }}>
          <input type="checkbox" checked={selected.includes(option)} onChange={() => onToggle(option)} />
          <span>{option}</span>
        </label>
      ))}
    </div>
  );
}

function ActiveFilters({ locations, categories, businessType, onRemoveLocation, onRemoveCategory, onClear }: {
  locations: string[]; categories: string[]; businessType: BusinessTypeFilter;
  onRemoveLocation: (value: string) => void; onRemoveCategory: (value: string) => void; onClear: () => void;
}) {
  const chips = [
    ...locations.map(value => ({ label: `Location: ${value}`, remove: () => onRemoveLocation(value) })),
    ...categories.map(value => ({ label: `Category: ${value}`, remove: () => onRemoveCategory(value) })),
    ...(businessType === 'all' ? [] : [{ label: businessType === 'single' ? 'Single-location' : 'Multi-location', remove: onClear }]),
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', margin: '-2px 0 12px' }}>
      {chips.map(chip => <button key={chip.label} onClick={chip.remove} style={{
        border: '1px solid rgba(18,181,196,0.38)', background: 'rgba(18,181,196,0.08)', color: T.cyanDim,
        borderRadius: 12, padding: '3px 8px', cursor: 'pointer', fontSize: 11,
      }}>{chip.label} ×</button>)}
      <button onClick={onClear} style={{ border: 'none', background: 'transparent', color: T.fgMuted, cursor: 'pointer', fontSize: 11 }}>Clear all</button>
    </div>
  );
}

function toggleValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter(item => item !== value) : [...values, value];
}

// 2-line clamp so wildly different name lengths don't make cards uneven.
const clamp2: React.CSSProperties = {
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
  overflow: 'hidden', wordBreak: 'break-word',
};

// Every card is exactly this tall regardless of how many locations it has —
// the location chips live in a fixed-height area that scrolls when it overflows.
const CARD_H = 176;

function locName(c: import('../types').Client): string {
  return c.locationLabel?.trim() || c.location;
}

// Small blue pill used to surface a single location, so every client (single
// or multi) shows its branches at a glance. Shared by the card + list views.
function LocationChip({ label }: { label: string }) {
  return (
    <span title={label} style={{
      fontSize: 10, fontWeight: 600, color: T.cyan,
      background: 'rgba(18,181,196,0.10)', border: '1px solid rgba(18,181,196,0.35)',
      borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap',
      maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
      display: 'inline-block', boxSizing: 'border-box',
    }}>{label}</span>
  );
}

function BusinessCard({ biz }: { biz: { head: import('../types').Client; locations: import('../types').Client[] } }) {
  const multi = biz.locations.length > 1;
  return (
    <Link to={`/admin/clients/${biz.head.id}`} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      <GBICard style={{ cursor: 'pointer', transition: 'transform 150ms', height: CARD_H, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 10 }}>
          <div title={biz.head.name} style={{ minWidth: 0, flex: 1, fontFamily: sharedStyles.fontDisplay, fontSize: 16, fontWeight: 700, color: T.fgPrimary, lineHeight: 1.3, ...clamp2 }}>{biz.head.name}</div>
          {multi ? (
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
              color: T.cyan, background: 'rgba(18,181,196,0.10)',
              border: `1px solid ${T.cyan}`, borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap',
            }}>{biz.locations.length} LOCATIONS</span>
          ) : (
            <GBIBadge color={T.cyan}>Active</GBIBadge>
          )}
        </div>
        {/* Fixed-height, scrollable chip area: every location is its own blue
            chip; if they overflow this region it scrolls inside the card so the
            card itself stays exactly CARD_H tall. */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto', marginRight: -6, paddingRight: 6,
          display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 6,
        }}>
          {biz.locations.map(l => <LocationChip key={l.id} label={locName(l)} />)}
        </div>
      </GBICard>
    </Link>
  );
}

function BusinessListTable({ businesses }: {
  businesses: { head: import('../types').Client; locations: import('../types').Client[] }[];
}) {
  return (
    <div style={{
      background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden',
    }}>
      {businesses.map((b, i) => {
        const multi = b.locations.length > 1;
        return (
        <Link key={b.head.id} to={`/admin/clients/${b.head.id}`} style={{ textDecoration: 'none' }}>
          <div
            style={{
              display: 'grid', gridTemplateColumns: '1fr auto',
              gap: 16, alignItems: 'center', padding: '12px 18px',
              borderTop: i === 0 ? 'none' : `1px solid ${T.border}`, cursor: 'pointer',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = T.bgSubtle; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontFamily: sharedStyles.fontDisplay, fontSize: 14, fontWeight: 700,
                color: T.fgPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{b.head.name}</div>
              {/* Every client lists its location(s) as blue chips, so the
                  number of branches is obvious at a glance. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {b.locations.map(l => <LocationChip key={l.id} label={locName(l)} />)}
              </div>
            </div>
            {multi
              ? <span style={{
                  fontSize: 10, fontWeight: 700, color: T.cyan,
                  background: 'rgba(18,181,196,0.10)', border: `1px solid ${T.cyan}`,
                  borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap',
                }}>{b.locations.length}</span>
              : <GBIBadge color={T.cyan}>Active</GBIBadge>}
          </div>
        </Link>
        );
      })}
    </div>
  );
}
